import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  KbPrincipal,
  KbSectionResponse,
  changeKbItemOwner,
  searchKbEligibleUsers,
} from "@/api/knowledge";
import { useMe } from "@/hooks/useMe";

/**
 * D-147 W1 S5 (дельта P2) — живий пікер «Відповідального» (мокап ④):
 * діє ОДРАЗУ (поза draft-снімком) з аудитом kb_item_owner_changed.
 * Кандидати за A2/backend-правилами (сервіс — SoT, FE лише пропонує):
 *   editor → себе · owner-grade → owner/backup/editors розділу ·
 *   KB_Admin → + пошук будь-якого eligible internal (active, без portal/QA).
 */
export function KbOwnerPicker({
  itemId,
  owner,
  section,
  onChanged,
}: {
  itemId: string;
  owner: KbPrincipal | null;
  section: KbSectionResponse | undefined;
  onChanged: () => void;
}) {
  const me = useMe();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adminQuery, setAdminQuery] = useState("");
  const [extra, setExtra] = useState<KbPrincipal[]>([]);

  const isKbAdmin = section?.capability === "admin";
  const ownerGrade = isKbAdmin || section?.capability === "owner";

  const candidates = useMemo(() => {
    const map = new Map<string, KbPrincipal>();
    const add = (p: KbPrincipal | null | undefined) => {
      if (p?.id) map.set(p.id, p);
    };
    add(owner);
    if (ownerGrade && section) {
      add(section.owner);
      add(section.backupOwner);
      for (const e of section.editors ?? []) add(e);
    }
    const meUser = me.data?.user;
    if (meUser) add({ id: meUser.id, name: meUser.name });
    for (const p of extra) add(p);
    return Array.from(map.values());
  }, [owner, section, ownerGrade, me.data?.user, extra]);

  const adminSearch = useQuery({
    queryKey: ["kbEligibleUsers", adminQuery],
    queryFn: () => searchKbEligibleUsers(adminQuery),
    enabled: isKbAdmin && adminQuery.trim().length >= 2,
    staleTime: 30_000,
  });

  const change = (ownerId: string) => {
    if (!ownerId || ownerId === owner?.id) return;
    setBusy(true);
    setError(null);
    changeKbItemOwner(itemId, ownerId)
      .then(onChanged)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "Не вдалося змінити відповідального."),
      )
      .finally(() => setBusy(false));
  };

  return (
    <div>
      <label className="mb-1 block text-xs font-semibold" htmlFor="kb-owner-picker">
        Відповідальний{" "}
        <span className="font-normal text-muted-foreground">(діє одразу, з аудитом)</span>
      </label>
      <select
        id="kb-owner-picker"
        value={owner?.id ?? ""}
        disabled={busy}
        onChange={(e) => change(e.target.value)}
        className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring disabled:opacity-60"
      >
        {candidates.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name || c.id}
          </option>
        ))}
      </select>

      {isKbAdmin && (
        <div className="mt-1.5">
          <input
            value={adminQuery}
            onChange={(e) => setAdminQuery(e.target.value)}
            placeholder="Пошук співробітника (KB_Admin)…"
            className="w-full rounded-lg border bg-background px-3 py-1.5 text-xs outline-none focus:ring-1 focus:ring-ring"
          />
          {(adminSearch.data?.users ?? []).length > 0 && (
            <ul className="mt-1 max-h-36 overflow-y-auto rounded-lg border bg-card text-xs">
              {adminSearch.data!.users.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    className="w-full px-3 py-1.5 text-left hover:bg-muted"
                    onClick={() => {
                      setExtra((prev) => [...prev, u]);
                      setAdminQuery("");
                      change(u.id);
                    }}
                  >
                    {u.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {error && (
        <p role="alert" className="mt-1 text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
